/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { ElementRef, Directive, Input, OnDestroy, Optional, AfterViewInit, NgZone } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subscription, Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FocusManager } from '../services/focus-manager';

/*
 * Transits focus back to the initial focusable child element upon pressing Escape key
 */
@Directive({
    selector: '[nvFocusBack]',
    exportAs: 'nvFocusBack',
    standalone: true
})
export class FocusBackDirective implements AfterViewInit, OnDestroy {
    @Input() get enableFocusBack() {
        return this._enableFocusBack;
    }
    set enableFocusBack(value: boolean) {
        this._enableFocusBack = coerceBooleanProperty(value);
    }
    private _enableFocusBack = true;

    private elementScrolledSub: Subscription;
    private destroy$ = new Subject<boolean>();

    private get scrollPosition(): number {
        return this.scroller?.measureScrollOffset();
    }

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private focusManager: FocusManager,
        private ngZone: NgZone,
        @Optional() private scroller: CdkVirtualScrollViewport
    ) {}

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        if (!this.enableFocusBack) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            fromEvent<KeyboardEvent>(this.elementRef.nativeElement, 'keydown')
                .pipe(
                    filter(event => event.key === 'Escape' && !event.defaultPrevented),
                    takeUntil(this.destroy$)
                )
                .subscribe(this.onKeydownEvent.bind(this));
        });
    }

    private onKeydownEvent(event: KeyboardEvent) {
        // early return if the escape key is not consumed
        if (!this.focusInitialFocusable()) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Move the focus to the initial focusable. If the scroll offset of the container is not 0,
     * scroll the container to 0 before moving the focus
     *
     * @returns {boolean} true if the directive moves the focus to a new element or
     * it is scrolling, otherwise false.
     */
    focusInitialFocusable(): boolean {
        if (this.scroller && this.scrollPosition !== 0) {
            // if the scroller is not scrolling, then start to scroll
            if (!this.elementScrolledSub) {
                this.scrollThenFocusInitialFocusable(this.scroller?.elementRef?.nativeElement);
            }
            return true;
        }
        const container = this.scroller?.elementRef?.nativeElement ?? this.elementRef?.nativeElement;
        const initialFocusable = this.focusManager.getInitialFocusable(container);
        if (initialFocusable === document.activeElement || !initialFocusable?.isConnected) {
            return false;
        }
        this.focusManager.focusViaLastOrigin(initialFocusable);
        return true;
    }

    private scrollThenFocusInitialFocusable(scrollerElement: HTMLElement) {
        let lastPosition = this.scrollPosition;
        this.elementScrolledSub = this.scroller
            .elementScrolled()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.scrollPosition === 0 || this.scrollPosition > lastPosition) {
                    if (this.scrollPosition === 0) {
                        this.focusManager.focusChild(scrollerElement, 'initial');
                    }
                    this.elementScrolledSub.unsubscribe();
                    this.elementScrolledSub = undefined;
                } else {
                    lastPosition = this.scrollPosition;
                }
            });

        this.scroller.scrollToIndex(0, 'auto');
    }
}
